/**
 * PATH: apps/qa/src/pages/RecordDetailPage.tsx
 * PURPOSE: QA inspection detail page. Checklist/Report flip via bottom dock.
 *
 * DESIGN: No sticky/fixed toggle bars. The view-mode flip button and context
 *         actions live inside the UniversalDock (bottom-right pill) via
 *         useDockActions(). One tap to flip between Checklist ↔ Report.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inspectionApi, floorApi } from '@/lib/api'
import { formatDateTime, cn } from '@/lib/utils'
import { useCloseOnOutsidePointer, useDockActions } from '@quotech/ui'
import { QA_PHONE_TITLE_ACTIONS_SLOT_ID, requestQaPhonePage, requestQaPhoneSheet } from '@quotech/job-hub/jobs/qa/phonePage'
import AreaCard from '@/components/AreaCard'
import { JobQaRecordWorkspace } from './record-detail/JobQaRecordWorkspace'
import { QaRecordItpLibrary } from './record-detail/QaRecordItpLibrary'
import ReportView from '@/components/ReportView'
import InspectionStructureDrawer from '@/components/InspectionStructureDrawer'
import {
  ArrowLeft, FileText, CheckCircle, Clock, Send,
  Loader2, MapPin, ListChecks, PackageCheck,
  ClipboardList, FileBarChart, Save, PenTool, Download,
  Layers, Users, ExternalLink, MoreVertical,
} from 'lucide-react'
import { jobQaTabUrl, normalizeList, qaInspectionStatusLabel } from '@quotech/job-hub/jobs/qa'
import { ApplyTemplateModal } from './record-detail/ApplyTemplateModal'
import { ParticipantsModal } from './record-detail/ParticipantsModal'
import { ProgressSummary } from './record-detail/ProgressSummary'
import { useQaTabStore } from '../config/tabs'

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  draft: { bg: 'bg-muted', text: 'text-muted-foreground', icon: <FileText className="w-4 h-4" /> },
  published: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: <Clock className="w-4 h-4" /> },
  in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: <Clock className="w-4 h-4" /> },
  ready_for_pack: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: <PackageCheck className="w-4 h-4" /> },
  submitted: { bg: 'bg-primary-100 dark:bg-primary-900/30', text: 'text-primary-700 dark:text-primary-400', icon: <Send className="w-4 h-4" /> },
  approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: <CheckCircle className="w-4 h-4" /> },
}

type ViewMode = 'checklist' | 'report'

export default function RecordDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateActiveTab = useQaTabStore(s => s.updateActiveTab)
  const { setActions, clearActions } = useDockActions()
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false)
  const [showParticipantsModal, setShowParticipantsModal] = useState(false)
  const [showStructureDrawer, setShowStructureDrawer] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('checklist')
  // Phone-only header overflow (Open in job / Publish / Submit live here on
  // max-md so the header stays one slim row). Desktop keeps the inline cluster.
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false)
  const openItpLibrary = useRef<(() => void) | null>(null)
  const bindItpLibrary = useCallback((open: () => void) => {
    openItpLibrary.current = open
  }, [])
  const phoneMenuRef = useRef<HTMLDivElement>(null)
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(phoneMenuOpen, () => setPhoneMenuOpen(false), [phoneMenuRef])
  const titleRowRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onPick = (event: Event) => {
      const nextId = (event as CustomEvent<{ id?: string }>).detail?.id
      if (!nextId || nextId === id) return
      navigate(`/records/${encodeURIComponent(nextId)}`)
    }
    window.addEventListener('qa-phone-inspection-picked', onPick)
    return () => window.removeEventListener('qa-phone-inspection-picked', onPick)
  }, [id, navigate])

  // Report action callbacks (set by ReportView via onActionsReady)
  const [reportActions, setReportActions] = useState<{
    onSave?: () => void
    onSignOff?: () => void
    onExport?: () => void
    isSaving?: boolean
    isExporting?: boolean
  }>({})

  const handleReportActions = useCallback((actions: typeof reportActions) => {
    setReportActions(actions)
  }, [])

  const { data: inspection, isLoading, error } = useQuery({
    queryKey: ['qa-inspection', id],
    queryFn: () => inspectionApi.getInspection(id!),
    enabled: !!id,
    placeholderData: keepPreviousData,
  })
  useEffect(() => {
    const row = titleRowRef.current
    if (!row) return
    const apply = () => {
      row.parentElement?.style.setProperty('--qa-record-title-h', `${row.offsetHeight}px`)
    }
    apply()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(apply)
    observer.observe(row)
    return () => observer.disconnect()
  }, [inspection?.id, isLoading])

  // `qa-floors` is also read by the embedded job-hub QA tab, which expects a
  // plain array. Whoever fetched first used to win the cache, so unwrapping
  // here instead of in the queryFn crashed the other reader. Normalize on the
  // way in: one query key, one shape.
  const { data: floors = [] } = useQuery({
    queryKey: ['qa-floors', id],
    queryFn: async () => normalizeList<Record<string, unknown>>(
      await floorApi.getFloors({ inspection_id: id! }),
    ),
    enabled: !!id,
  })

  useEffect(() => {
    if (inspection?.inspection_name) {
      updateActiveTab(location.pathname, inspection.inspection_name)
    }
  }, [inspection?.inspection_name, location.pathname, updateActiveTab])

  const publishMutation = useMutation({
    mutationFn: () => inspectionApi.publishInspection(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-inspection', id] }) },
  })

  const submitMutation = useMutation({
    mutationFn: () => inspectionApi.submitInspection(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-inspection', id] }); setShowSubmitModal(false) },
  })

  // ── Dock actions: flip button + context actions ────────────────────────
  useEffect(() => {
    const actions: Array<{ id: string; label: string; icon: React.ReactNode; onClick: () => void; color?: 'teal' | 'blue' | 'amber' | 'purple' | 'rose' }> = []

    // Structure drawer toggle (always available)
    actions.push({
      id: 'structure-drawer',
      label: 'Structure',
      icon: <Layers className="w-3.5 h-3.5" />,
      onClick: () => setShowStructureDrawer(prev => !prev),
      color: 'teal',
    })

    // Job team (per-job QA role grants) — needs the job pk from the detail payload
    if (inspection?.job) {
      actions.push({
        id: 'job-team',
        label: 'Team',
        icon: <Users className="w-3.5 h-3.5" />,
        onClick: () => setShowParticipantsModal(true),
        color: 'amber',
      })
    }

    if (viewMode === 'checklist') {
      // Flip to Report
      actions.push({
        id: 'flip-view',
        label: 'Report',
        icon: <FileBarChart className="w-3.5 h-3.5" />,
        onClick: () => setViewMode('report'),
        color: 'purple',
      })
      // Apply Template
      actions.push({
        id: 'apply-template',
        label: 'Template',
        icon: <ListChecks className="w-3.5 h-3.5" />,
        onClick: () => setShowApplyTemplateModal(true),
        color: 'blue',
      })
    } else {
      // Flip to Checklist
      actions.push({
        id: 'flip-view',
        label: 'Checklist',
        icon: <ClipboardList className="w-3.5 h-3.5" />,
        onClick: () => setViewMode('checklist'),
        color: 'purple',
      })
      // Report context actions
      if (reportActions.onSave) {
        actions.push({
          id: 'save-report',
          label: reportActions.isSaving ? 'Saving...' : 'Save',
          icon: reportActions.isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />,
          onClick: reportActions.onSave,
          color: 'blue',
        })
      }
      if (reportActions.onSignOff) {
        actions.push({
          id: 'sign-off',
          label: 'Sign Off',
          icon: <PenTool className="w-3.5 h-3.5" />,
          onClick: reportActions.onSignOff,
          color: 'teal',
        })
      }
      if (reportActions.onExport) {
        actions.push({
          id: 'export-pdf',
          label: reportActions.isExporting ? 'Exporting...' : 'Export',
          icon: reportActions.isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />,
          onClick: reportActions.onExport,
          color: 'amber',
        })
      }
    }

    setActions(actions)
    return () => clearActions()
  }, [viewMode, reportActions, setActions, clearActions, showStructureDrawer, inspection?.job])

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  }

  if (error || !inspection) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center"><FileText className="w-8 h-8 text-muted-foreground" /></div>
        <p className="text-foreground font-medium">Inspection not found</p>
        <p className="text-sm text-muted-foreground mt-1">{id ? `No inspection found with ID: ${id}` : 'Please select an inspection to view'}</p>
        <Link to="/records" className="mt-4 inline-flex items-center gap-2 text-primary hover:text-primary-600 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to inspections</Link>
      </div>
    )
  }

  const style = STATUS_STYLES[inspection.status] || STATUS_STYLES.draft
  const hasJob = Number(inspection.job_id) > 0
  const phoneChecklist = viewMode === 'checklist' && hasJob

  return (
    <div data-qa-record-page="" className={cn('space-y-4 max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-col max-md:space-y-0', phoneChecklist ? 'max-md:overflow-hidden' : 'max-md:overflow-y-auto max-md:[&>*]:shrink-0')}>
      {/* Header. Phone (max-md): one slim row — back + name + overflow menu;
          Created line, chips and buttons collapse into the menu. Desktop
          unchanged. */}
      <div ref={titleRowRef} className={cn('flex items-center justify-between gap-2 px-1 md:gap-4 max-md:sticky max-md:top-0 max-md:mx-[var(--qa-phone-inset,0px)] max-md:shrink-0 max-md:bg-background max-md:pb-1', phoneMenuOpen ? 'max-md:z-50' : 'max-md:z-20')}>
        <div className="flex min-w-0 items-center gap-2 md:gap-4">
          <button onClick={() => navigate('/records')} className="shrink-0 p-2 rounded-lg hover:bg-accent transition-colors"><ArrowLeft className="w-5 h-5 text-muted-foreground" /></button>
          <div className="min-w-0 md:flex md:flex-wrap md:items-center md:gap-x-3">
            <button
              type="button"
              className="block max-w-full min-h-9 min-w-0 flex-1 truncate text-left text-base font-bold text-foreground md:hidden"
              aria-label={`Choose QA, current ${inspection.inspection_name || 'Untitled Inspection'}`}
              onClick={() => requestQaPhoneSheet('select-qa')}
            >
              {inspection.inspection_name || 'Untitled Inspection'}
            </button>
            <h1 className="hidden min-w-0 truncate text-base font-bold text-foreground md:block md:text-2xl">{inspection.inspection_name || 'Untitled Inspection'}</h1>
            {hasJob ? (
              <QaRecordItpLibrary jobId={Number(inspection.job_id)} bindOpen={bindItpLibrary} />
            ) : null}
            <p className="text-muted-foreground text-sm max-md:hidden md:basis-full">Created {formatDateTime(inspection.created_at)}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 md:hidden">
          <div id={QA_PHONE_TITLE_ACTIONS_SLOT_ID} className="flex items-center" />
        <div ref={phoneMenuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setPhoneMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={phoneMenuOpen}
            aria-label="Inspection actions"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
          {phoneMenuOpen && (
            <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-xl">
              <p className={cn('mx-3 my-1.5 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium', style.bg, style.text)}>
                {qaInspectionStatusLabel(inspection.status)}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPhoneMenuOpen(false)
                  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
                  requestQaPhonePage('setup')
                }}
                className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm text-foreground hover:bg-accent"
              >
                Start/Add/Edit
              </button>
              {inspection.job_id && (
                <a
                  href={jobQaTabUrl(inspection.job_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-[44px] items-center gap-2 px-3 text-sm text-foreground hover:bg-accent"
                  onClick={() => setPhoneMenuOpen(false)}
                >
                  <ExternalLink className="h-4 w-4" /> Open in job
                </a>
              )}
              {hasJob ? (
                <button
                  type="button"
                  onClick={() => {
                    setPhoneMenuOpen(false)
                    openItpLibrary.current?.()
                  }}
                  className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm text-foreground hover:bg-accent"
                >
                  <FileText className="h-4 w-4" /> ITP Library
                </button>
              ) : null}
              {inspection.status === 'draft' && (
                <button
                  type="button"
                  onClick={() => { setPhoneMenuOpen(false); publishMutation.mutate() }}
                  disabled={publishMutation.isPending}
                  className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Publish
                </button>
              )}
              {inspection.status === 'ready_for_pack' && (
                <button
                  type="button"
                  onClick={() => { setPhoneMenuOpen(false); setShowSubmitModal(true) }}
                  disabled={submitMutation.isPending}
                  className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" /> Submit
                </button>
              )}
            </div>
          )}
        </div>
        </div>
        <div className="flex items-center gap-3 max-md:hidden">
          {inspection.job_id && (
            <a
              href={jobQaTabUrl(inspection.job_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-input text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title={`Open job ${inspection.job_id} QA in the job hub`}
            >
              <ExternalLink className="w-4 h-4" /> Open in job
            </a>
          )}
          <span className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium', style.bg, style.text)}>
            {style.icon} {qaInspectionStatusLabel(inspection.status)}
          </span>
          {inspection.status === 'draft' && (
            <button onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500 text-white font-medium text-sm hover:bg-purple-600 transition-colors disabled:opacity-50">
              {publishMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publish
            </button>
          )}
          {inspection.status === 'ready_for_pack' && (
            <button onClick={() => setShowSubmitModal(true)} disabled={submitMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50">
              {submitMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Submit
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'checklist' && Number(inspection.job_id) > 0 && (
        <JobQaRecordWorkspace
          jobId={Number(inspection.job_id)}
          focusInspectionId={inspection.id}
        />
      )}

      {viewMode === 'checklist' && !(Number(inspection.job_id) > 0) && (
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden max-md:mx-[var(--qa-phone-inset,0px)]">
          {floors?.length ? (
            <div className="divide-y divide-border">
              {floors.map((floor: Record<string, unknown>) => (
                <div key={floor.id as string} id={`floor-${floor.id}`} className="p-4 transition-all duration-500">
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {floor.floor_name as string}</h3>
                  {(floor.areas as Record<string, unknown>[])?.length ? (
                    <div className="grid grid-cols-1 gap-3">
                      {(floor.areas as Record<string, unknown>[]).map((area: Record<string, unknown>) => (
                        <div key={area.id as string} id={`area-${area.id}`} className="transition-all duration-500">
                          <AreaCard area={{
                            id: String(area.id ?? ''),
                            area_name: String(area.area_name ?? ''),
                            area_code: String(area.area_code ?? ''),
                            status: String(area.status ?? ''),
                            is_locked: Boolean(area.is_locked),
                          }} inspectionId={id!}
                            onStatusChange={() => { queryClient.invalidateQueries({ queryKey: ['qa-floors', id] }); queryClient.invalidateQueries({ queryKey: ['qa-inspection', id] }) }} />
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No areas defined</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center"><MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground" /><p className="text-muted-foreground">No floors defined for this inspection</p></div>
          )}
        </div>
      )}

      {viewMode === 'report' && (
        <div className="max-md:mx-[var(--qa-phone-inset,0px)]">
          <ReportView
            inspectionId={id!}
            inspectionName={inspection.inspection_name || 'Untitled Inspection'}
            inspection={inspection}
            floors={floors}
            onActionsReady={handleReportActions}
          />
        </div>
      )}

      {/* Phone: the workspace header already shows scoped progress; this
          summary card would spend a screen of vertical space repeating it. */}
      <div className="max-md:hidden">
        <ProgressSummary inspection={inspection} />
      </div>

      {/* Submit modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
            <h3 className="text-lg font-semibold text-foreground mb-4">Submit Inspection</h3>
            <p className="text-muted-foreground mb-4">Are you sure you want to submit this inspection for review? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowSubmitModal(false)} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
              <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}
                className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50">
                {submitMutation.isPending ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showParticipantsModal && inspection.job && (
        <ParticipantsModal
          jobId={Number(inspection.job)}
          jobName={inspection.job_name}
          onClose={() => setShowParticipantsModal(false)}
        />
      )}

      {showApplyTemplateModal && (
        <ApplyTemplateModal inspectionId={id!} floors={floors}
          onClose={() => setShowApplyTemplateModal(false)}
          onSuccess={() => { setShowApplyTemplateModal(false); queryClient.invalidateQueries({ queryKey: ['qa-floors', id] }); queryClient.invalidateQueries({ queryKey: ['qa-inspection', id] }) }} />
      )}

      {/* Structure Drawer */}
      <InspectionStructureDrawer
        inspectionId={id!}
        open={showStructureDrawer}
        onClose={() => setShowStructureDrawer(false)}
        onApplyTemplate={(_areaIds) => {
          setShowApplyTemplateModal(true)
        }}
      />
    </div>
  )
}
